// The card's aggregator: when the reader's middlegames turn. For each scanned
// game of the period that reached a middlegame, the pawns the reader's own
// middlegame moves gave up in each band of move numbers
// (`middlegameLossByTurn`, from the middlegame's curve, whose scores are
// capped at ±10 pawns by the facts' `SCORE_CAP`). A band's measure is the
// average: those pawns summed over the games that played moves there
// (`middlegameTurnsPlayed`), divided by those games, so a band every
// middlegame reaches and one few reach read on the same scale (2026-10-10;
// it was the plain sum until then). Apart from that, a band counts the games
// that turned there, giving up at least half a pawn (`middlegameTurnsOf`, the
// rule the library's `middlegame-turn` filter resolves with), so the column's
// link opens exactly that many games.
// Registered with the game store by this plugin's setup; the store runs it
// over the last 30 days' games.
import {
  aggregatorId,
  middlegameLossByTurn,
  middlegameTurnsPlayed,
  MIDDLEGAME_TURNS,
  MIDDLEGAME_TURN_PAWNS,
  type Accumulator,
  type Aggregator,
  type MiddlegameTurn,
} from '@chess/api-model';

/** One band of move numbers. */
export interface TurnBand {
  band: MiddlegameTurn;
  /** Middlegames the reader played at least one scored move of in the band: the average's divisor. */
  played: number;
  /** Of those, the games that gave up at least `MIDDLEGAME_TURN_PAWNS` in the band: what the column's link opens. */
  turned: number;
  /** Pawns, positive: what the reader's moves in the band gave up per middlegame played there, to the hundredth; 0 when none was. */
  pawns: number;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface MiddlegameTurnsResult {
  /** Scanned games of the period that reached a middlegame. */
  middlegames: number;
  /** Every band, in move order. */
  bands: TurnBand[];
}

const perBand = () => Object.fromEntries(MIDDLEGAME_TURNS.map((b) => [b, 0])) as Record<MiddlegameTurn, number>;

export class MiddlegameTurnsAggregator implements Aggregator<MiddlegameTurnsResult> {
  readonly id = aggregatorId('middlegame-turns');

  start(): Accumulator<MiddlegameTurnsResult> {
    let middlegames = 0;
    const played = perBand();
    const turned = perBand();
    const lost = perBand();
    return {
      add: (game) => {
        if (!game.scanned || !game.stageCurves?.middlegame) return;
        middlegames += 1;
        const loss = middlegameLossByTurn(game);
        for (const band of middlegameTurnsPlayed(game)) {
          played[band] += 1;
          lost[band] += loss[band] ?? 0;
          if ((loss[band] ?? 0) >= MIDDLEGAME_TURN_PAWNS) turned[band] += 1;
        }
      },
      result: () => ({
        middlegames,
        bands: MIDDLEGAME_TURNS.map((band) => ({
          band,
          played: played[band],
          turned: turned[band],
          pawns: played[band] ? Math.round((100 * lost[band]) / played[band]) / 100 : 0,
        })),
      }),
    };
  }
}
